// file: src/components/viewers/PDFViewer.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useFileDataCache } from '../../hooks/useFileDataCache';
import { useDocNavigation } from '../../hooks/useOpenCitation';
import { setJsonLocalForage } from '../../utils/localForageUtils';
import styles from './PdfViewer.module.css';

// PDF navigation and zoom icons from Lucide
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

// Important: Worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function PdfViewer({ file, pageToGo, pageJump }) {
  const { fileData, loading, error } = useFileDataCache(file.id);
  const containerRef = useRef(null);
  const fileDataRef = useRef(null);
  const scrollDoneRef = useRef(false);
  const [scale, setScale] = useState(1.0);
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 2.5;
  const SCALE_STEP = 0.1;

  // Use the extracted PDF navigation hook
  const {
    currentPage,
    numPages,
    setNumPages,
    goToPage,
    formatPageInfo
  } = useDocNavigation({
    containerRef,
    pageClass: styles.pageContainer,
    onPageChange: (page) => {
      // Persist page change
      if (file?.id && page > 0 && numPages) {
        setJsonLocalForage(`pageNumber-${file.id}`, page);
      }
    }
  });

  // Convert file data for react-pdf, avoiding unnecessary copies
  const fileForReactPdf = useMemo(() => {
    if (!fileData) return null;

    // Check if we already have this data cached
    if (fileDataRef.current && fileDataRef.current.sourceData === fileData) {
      return fileDataRef.current.pdfData;
    }

    let pdfData;
    
    // Create a view without copying the underlying buffer
    if (fileData instanceof ArrayBuffer) {
      pdfData = { data: new Uint8Array(fileData) };
    } else if (fileData instanceof Uint8Array) {
      // Use the existing Uint8Array directly
      pdfData = { data: fileData };
    } else {
      console.error("[PDF Data] Unexpected fileData type:", typeof fileData);
      pdfData = { data: fileData };
    }

    // Cache the reference to avoid recreating
    fileDataRef.current = { sourceData: fileData, pdfData };
    return pdfData;

  }, [fileData]);

  function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf.numPages);
  }

  function onDocumentLoadError(err) {
    console.error('PdfViewer load error:', err);
    if (err && err.message && err.message.includes('ArrayBuffer')) {
      console.warn('ArrayBuffer error detected. PDF data type:', 
        fileData ? (fileData instanceof Uint8Array ? 'Uint8Array' : 
                   (fileData instanceof ArrayBuffer ? 'ArrayBuffer' : typeof fileData)) : 'null',
        'Size:', fileData ? (fileData.byteLength || fileData.length || 'unknown') : 'N/A');
    }
    // Clear numPages on error to prevent inconsistent state
    setNumPages(null); 
    // Clear cached data on error
    fileDataRef.current = null;
  }

  // Page navigation handlers

  function goToPrevPage() {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }

  function goToNextPage() {
    if (currentPage < numPages) {
      goToPage(currentPage + 1);
    }
  }

  function handleZoomIn() {
    setScale((s) => Math.min(MAX_SCALE, Math.round((s + SCALE_STEP) * 100) / 100));
  }
  function handleZoomOut() {
    setScale((s) => Math.max(MIN_SCALE, Math.round((s - SCALE_STEP) * 100) / 100));
  }

  // Effect specifically for handling external page change requests (citations)
  useEffect(() => {
    if (pageToGo == null || !numPages || pageToGo > numPages) {
      if (pageToGo > numPages) {
        console.warn(`[pageToGo Effect] Invalid page number ${pageToGo} for document with ${numPages} pages.`);
      }
      return;
    }

    // Check if the page element already exists in the DOM
    const pages = containerRef.current?.getElementsByClassName(styles.pageContainer) || [];
    if (pages[pageToGo - 1]) {
      // Page is already rendered - jump immediately
      goToPage(pageToGo, pageJump?.instant);
      return;
    }

    // Page not yet rendered - wait for onRenderSuccess callback
    scrollDoneRef.current = false;
  }, [pageToGo, numPages, goToPage, pageJump?.timestamp, pageJump?.instant]); 


  if (loading) {
    return <div className={styles.loadingMessage}>Loading PDF data...</div>;
  }
  if (error) {
    return <div className={styles.errorMessage}>Failed to load PDF: {error.message}</div>;
  }
  if (!fileData) {
    return <div className={styles.loadingMessage}>No PDF data yet...</div>;
  }

  return (
    <div className={styles.container}>
      <div ref={containerRef} className={styles.singlePageContainer}>
        <Document
          file={fileForReactPdf}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          key={`pdf-doc-${file.id}`}
        >
          {Array.from(new Array(numPages), (_, i) => {
            const pageNumber = i + 1;
            return (
              <div 
                key={`page_${i + 1}`} 
                className={styles.pageContainer}
              >
                <Page
                  pageNumber={i + 1}
                  width={800 * scale}
                  scale={scale}
                  renderTextLayer
                  renderAnnotationLayer
                  loading={
                    <div className={styles.loadingMessage}>
                      Loading page {i + 1}...
                    </div>
                  }
                  onRenderSuccess={() => {
                    // When the target page is rendered, scroll to it
                    if (pageToGo === i + 1 && !scrollDoneRef.current) {
                      scrollDoneRef.current = true;
                      requestAnimationFrame(() => goToPage(pageToGo, pageJump?.instant));
                    }
                  }}
                />
              </div>
            );
          })}
        </Document>
      </div>

      {numPages > 1 && (
        <div className={styles.navigationBar}>
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className={styles.pageButton}
          >
            <ChevronLeft size={20} className={currentPage <= 1 ? 'opacity-50' : ''} />
          </button>

          <div className={styles.pageInfo}>
            {formatPageInfo()}
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= numPages}
            className={styles.pageButton}
          >
            <ChevronRight size={20} className={currentPage >= numPages ? 'opacity-50' : ''} />
          </button>

          {/* Zoom out button */}
          <button
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
            className={styles.pageButton}
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          {/* Zoom in button */}
          <button
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
            className={styles.pageButton}
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default React.memo(PdfViewer);






